import { useState } from "react";

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
} from "@mui/material";
import { parseExcelPaste } from "../utils/parseExcelPaste";

type Props = {
    open: boolean;
    onClose: () => void;
    onImported: () => void;
};

export default function ExcelImportDialog({
    open,
    onClose,
    onImported,
}: Props) {
    const [content, setContent] = useState("");
    const [preview, setPreview] = useState<any[]>([]);
    // const [existingJudokas, setExistingJudokas] = useState<any[]>([]);

    const isLocal = window.location.hostname === "localhost";
    const API_URL = isLocal ? "http://localhost:7881/api" // backend local sans /api 
        : "/api"; // backend prod avec /api


    const loadExistingJudokas = async () => {

        const registrationCode =
            localStorage.getItem(
                "registrationCode"
            );

        if (!registrationCode) {
            return [];
        }

        const response = await fetch(
            `${API_URL}/registration/${registrationCode}`
        );

        const data = await response.json();

        return data.judokas ?? [];
    };

    const validateJudoka = (judoka: any, existingJudokas: any[]) => {
        const errors: string[] = [];
        const warnings: string[] = [];

        const alreadyExists =
            existingJudokas.some(
                (j) => j.licence === judoka.licence
            );

        if (alreadyExists) {
            warnings.push(
                "Déjà inscrit"
            );
        }

        if (!judoka.email)
            errors.push("Email manquant");

        if (!judoka.licence)
            errors.push("Licence manquante");

        if (!/^\d{6}$/.test(judoka.licence))
            errors.push("Licence invalide");

        if (!judoka.lastName)
            errors.push("Nom manquant");

        if (!judoka.firstName)
            errors.push("Prénom manquant");

        if (!judoka.birthDate)
            errors.push("Date naissance manquante");

        if (!judoka.clubNumber)
            errors.push("N° club manquant");

        if (!judoka.clubName)
            errors.push("Nom club manquant");

        return {
            ...judoka,
            errors,
            warnings,
        };
    };

    const rowsToImport =
        preview.filter(
            (p) =>
                p.errors.length === 0 &&
                p.warnings.length === 0
        );

    const handleClose = () => {
        setContent("");
        setPreview([]);

        onClose();
    };
    const importJudokas = async () => {

        const registrationCode =
            localStorage.getItem(
                "registrationCode"
            );

        if (!registrationCode) {
            return;
        }

        for (const judoka of rowsToImport) {

            await fetch(
                `${API_URL}/registration/${registrationCode}/judokas`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        email: judoka.email,
                        licence: judoka.licence,
                        firstName: judoka.firstName,
                        lastName: judoka.lastName,
                        gender: judoka.gender,
                        nationality: judoka.nationality,
                        birthDate: judoka.birthDate,
                        category: judoka.category,
                        clubNumber:
                            judoka.clubNumber,
                        clubName:
                            judoka.clubName,
                    }),
                }
            );
        }

        await onImported();
        handleClose();
    };

    const generatePreview = async () => {

        const existing =
            await loadExistingJudokas();

        const parsed =
            parseExcelPaste(content);

        const validated = parsed.map(
            (j) =>
                validateJudoka(
                    j,
                    existing
                )
        );
        setPreview(validated);
    };

    const validRows = preview.filter(
        (p) => p.errors.length === 0
    ).length;

    const invalidRows = preview.filter(
        (p) => p.errors.length > 0
    ).length;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                Import Excel
            </DialogTitle>

            <DialogContent>
                <TextField
                    multiline
                    minRows={10}
                    fullWidth
                    label="Coller ici les données Excel"
                    value={content}
                    onChange={(e) =>
                        setContent(e.target.value)
                    }
                />
            </DialogContent>

            {preview.length > 0 && (
                <>
                    <Paper
                        sx={{
                            p: 2,
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        <strong>
                            {preview.length} ligne(s) analysée(s)
                        </strong>

                        <div>
                            ✅ {validRows} valide(s)
                        </div>

                        <div>
                            ❌ {invalidRows} invalide(s)
                        </div>
                    </Paper>

                    <TableContainer
                        component={Paper}
                        sx={{ mt: 3 }}
                    >
                        <Table size="small">

                            <TableHead>
                                <TableRow>
                                    <TableCell>Licence</TableCell>
                                    <TableCell>Nom</TableCell>
                                    <TableCell>Prénom</TableCell>
                                    <TableCell>Sexe</TableCell>
                                    <TableCell>Catégorie</TableCell>
                                    <TableCell>Club</TableCell>
                                    <TableCell>Validation</TableCell>
                                    <TableCell>Statut</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {preview.map((judoka, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            bgcolor:
                                                judoka.errors.length > 0
                                                    ? "#ffebee"
                                                    : undefined,
                                        }}
                                    >
                                        <TableCell>
                                            {judoka.licence}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.lastName}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.firstName}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.gender}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.category}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.clubName}
                                        </TableCell>

                                        <TableCell>
                                            {judoka.errors.length === 0
                                                ? "✅ OK"
                                                : judoka.errors.join(", ")
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {judoka.errors.length > 0
                                                ? judoka.errors.join(", ")
                                                : judoka.warnings.length > 0
                                                    ? "⚠ " +
                                                    judoka.warnings.join(", ")
                                                    : "✅ OK"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>

                        </Table>
                    </TableContainer>
                </>
            )}

            <DialogActions>
                <Button onClick={handleClose}>
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={generatePreview}
                >
                    {preview.length === 0
                        ? "Prévisualiser"
                        : "Rafraîchir la prévisualisation"}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={
                        preview.filter(
                            (p) =>
                                p.errors.length === 0 &&
                                p.warnings.length === 0
                        ).length === 0
                    }
                    onClick={importJudokas}
                >
                    Importer (
                    {
                        preview.filter(
                            (p) =>
                                p.errors.length === 0 &&
                                p.warnings.length === 0
                        ).length
                    }
                    )
                </Button>
            </DialogActions>
        </Dialog>
    );
}